import type { ReactNode } from 'react';

import { cn } from '@nucleus-commerce/ui/lib/utils';

export type StatusTone = 'success' | 'warning' | 'destructive' | 'info' | 'muted';

const TONES: Record<StatusTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  info: 'bg-info',
  muted: 'bg-muted-foreground/60',
};

export interface StatusDotProps {
  tone: StatusTone;
  /** Text next to the dot. Without it the dot is decorative and needs an aria-label upstream. */
  children?: ReactNode;
  /** Soft ping animation for "checking" or "live" states. */
  pulse?: boolean;
  className?: string;
}

/** Operator-console status marker: a small coloured dot with an optional label. */
export function StatusDot({ tone, children, pulse = false, className }: StatusDotProps) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-sm', className)}>
      <span className="relative flex size-2 shrink-0" aria-hidden>
        {pulse ? (
          <span
            className={cn(
              'absolute inline-flex size-full animate-ping rounded-full opacity-60',
              TONES[tone],
            )}
          />
        ) : null}
        <span className={cn('relative inline-flex size-2 rounded-full', TONES[tone])} />
      </span>
      {children !== undefined ? <span className="truncate">{children}</span> : null}
    </span>
  );
}
